import { InitialsAvatar } from '@alison/ui';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { memo, type ReactNode } from 'react';
import { initialsFrom } from '../../lib/format.js';
import { JOB_TITLES, tableColumns } from './constants.js';
import { MemberRoleBadge } from './MemberRoleBadge.js';
import { MemberRowActions } from './MemberRowActions.js';
import { MemberStatusBadge } from './MemberStatusBadge.js';
import type { JobTitleId, MemberRow } from './types.js';

const jobSelectSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    width: '80%',
    height: 32,
    borderRadius: 1,
    fontSize: 12,
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1.5,
      borderColor: 'divider',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },
  },
  '& .MuiOutlinedInput-input': { py: 0, px: 2 },
};

const AVATAR_SIZE = 36;

function jobLabel(jobId: string | null): string {
  if (jobId === null) return 'No job title';
  return JOB_TITLES.find((jobTitle) => jobTitle.id === jobId)?.label ?? jobId;
}

interface MemberRowViewProps {
  row: MemberRow;
  isLast: boolean;
  isAdmin: boolean;
  viewerIsAlisonStaff: boolean;
  onChangeJob: (userId: number, job: JobTitleId | null) => void;
  onMakeAdmin: (userId: number, name: string) => void;
  onRemove: (userId: number, name: string) => void;
  onRevoke: (inviteId: number, name: string) => void;
  showOrganization: boolean;
  onResend: (inviteId: number) => void;
}

export const MemberRowView = memo(
  function MemberRowView({
    row,
    isLast,
    isAdmin,
    viewerIsAlisonStaff,
    onChangeJob,
    onMakeAdmin,
    onRemove,
    onRevoke,
    onResend,
    showOrganization,
  }: MemberRowViewProps): ReactNode {
    // Client admins must not act on Alison staff who reach their workspace
    // without a grant; Alison staff keep managing their own rows, or a mis-set
    // staff role would only be fixable in the database.
    const isLockedAlisonStaff =
      row.isAlisonStaff && row.status === 'active' && !viewerIsAlisonStaff;
    const initials = initialsFrom(row.name, row.email) ?? '';
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: tableColumns,
          gap: 2,
          alignItems: 'center',
          py: 2,
          px: 3,
          fontSize: 13,
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'divider',
          bgcolor: row.status === 'invited' ? 'background.page' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InitialsAvatar
            variant="rounded"
            size={AVATAR_SIZE}
            initials={initials}
            sx={{ borderRadius: 1 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
              {row.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{row.email}</Typography>
            {showOrganization && row.organizationName !== null && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {row.organizationName}
              </Typography>
            )}
          </Box>
        </Box>
        {isAdmin &&
        row.status === 'active' &&
        row.userId !== null &&
        row.canEdit &&
        !isLockedAlisonStaff ? (
          <TextField
            select
            fullWidth
            size="small"
            value={row.job ?? ''}
            onChange={(event) =>
              onChangeJob(
                row.userId as number,
                event.target.value === '' ? null : (event.target.value as JobTitleId),
              )
            }
            slotProps={{ select: { displayEmpty: true } }}
            data-testid={`select-job-${row.key}`}
            sx={jobSelectSx}
          >
            <MenuItem value="">
              <Box component="span">No job title</Box>
            </MenuItem>
            {JOB_TITLES.map((jobTitle) => (
              <MenuItem key={jobTitle.id} value={jobTitle.id}>
                {jobTitle.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
            {jobLabel(row.job)}
          </Typography>
        )}
        <Box>
          <MemberRoleBadge role={row.role} />
        </Box>
        <Box>
          <MemberStatusBadge status={row.status} />
        </Box>
        {isAdmin && !isLockedAlisonStaff ? (
          <MemberRowActions
            row={row}
            onMakeAdmin={(userId) => onMakeAdmin(userId, row.name)}
            onRemove={(userId) => onRemove(userId, row.name)}
            onRevoke={(inviteId) => onRevoke(inviteId, row.name)}
            onResend={onResend}
          />
        ) : (
          <Box sx={{ textAlign: 'right', color: 'text.disabled' }}>-</Box>
        )}
      </Box>
    );
  },
  (prev, next) =>
    prev.isLast === next.isLast &&
    prev.isAdmin === next.isAdmin &&
    prev.viewerIsAlisonStaff === next.viewerIsAlisonStaff &&
    prev.showOrganization === next.showOrganization &&
    prev.onChangeJob === next.onChangeJob &&
    prev.onMakeAdmin === next.onMakeAdmin &&
    prev.onRemove === next.onRemove &&
    prev.onRevoke === next.onRevoke &&
    prev.onResend === next.onResend &&
    // Rows are useMemo'd, so an unchanged data set yields the same reference and
    // short-circuits here; only after a refetch do we fall back to field checks,
    // which keep re-renders scoped to the rows that actually changed.
    (prev.row === next.row ||
      (prev.row.key === next.row.key &&
        prev.row.name === next.row.name &&
        prev.row.email === next.row.email &&
        prev.row.job === next.row.job &&
        prev.row.role === next.row.role &&
        prev.row.status === next.row.status &&
        prev.row.organizationName === next.row.organizationName &&
        prev.row.userId === next.row.userId &&
        prev.row.inviteId === next.row.inviteId &&
        prev.row.isAlisonStaff === next.row.isAlisonStaff &&
        prev.row.canEdit === next.row.canEdit)),
);
